import { Component, Input, OnChanges, OnDestroy } from '@angular/core';
import { CardLinkAnalyticsService } from 'src/app/service/card-link-analytics.service';

@Component({
  selector: 'app-product-carousel-other',
  templateUrl: './product-carousel-other.component.html',
  styleUrls: ['./product-carousel-other.component.scss'],
})
export class ProductCarouselOtherComponent implements OnChanges, OnDestroy {
  @Input() products: any[] = [];
  @Input() lang: string = 'en';
  @Input() dir: string = '';
  @Input() cardNo: string = '';

  @Input() productsPerPage: number = 1;
  @Input() cardClass: string = '';
  @Input() titleColor: string = '#FFFFFF';
  @Input() descriptionColor: string = '#000000';
  @Input() iconColor: string = '#333333';
  @Input() showLinks: boolean = true;

  currentProductIndex = 0;
  private touchStartX: number | null = null;
  private autoScrollTimer: ReturnType<typeof setInterval> | null = null;
  private readonly autoScrollMs = 3500;

  constructor(private cardLinkAnalytics: CardLinkAnalyticsService) {}

  get dirClass(): string {
    const explicit = `${this.dir ?? ''}`.trim().toLowerCase();
    if (explicit === 'rtl' || explicit === 'ltr') {
      return explicit;
    }
    const code = `${this.lang ?? ''}`.trim().toLowerCase();
    return code === 'ar' || code.startsWith('ar-') ? 'rtl' : 'ltr';
  }

  get isRtl(): boolean {
    return this.dirClass === 'rtl';
  }

  get itemsPerPage(): number {
    const total = this.products?.length ?? 0;
    return Math.min(total || this.productsPerPage, this.productsPerPage);
  }

  get maxIndex(): number {
    const total = this.products?.length ?? 0;
    return Math.max(0, total - this.itemsPerPage);
  }

  get pageIndexes(): number[] {
    const pages = this.maxIndex + 1;
    return Array.from({ length: pages }, (_, i) => i);
  }

  ngOnChanges(): void {
    this.currentProductIndex = Math.min(this.currentProductIndex, this.maxIndex);
    this.preloadProductImages();
    this.restartAutoScroll();
  }

  private preloadProductImages(): void {
    for (const p of this.products ?? []) {
      const url = typeof p?.productImage === 'string' ? p.productImage.trim() : '';
      if (!url || url.startsWith('data:')) {
        continue;
      }
      const img = new Image();
      img.src = url;
    }
  }

  ngOnDestroy(): void {
    this.stopAutoScroll();
  }

  previous(): void {
    if (this.currentProductIndex > 0) {
      this.currentProductIndex--;
    }
  }

  next(): void {
    if (this.currentProductIndex < this.maxIndex) {
      this.currentProductIndex++;
    } else {
      this.currentProductIndex = 0;
    }
  }

  goTo(index: number): void {
    this.currentProductIndex = Math.max(0, Math.min(index, this.maxIndex));
  }

  onTouchStart(e: TouchEvent): void {
    const x = e.touches?.[0]?.clientX;
    this.touchStartX = typeof x === 'number' ? x : null;
  }

  onTouchEnd(e: TouchEvent): void {
    const startX = this.touchStartX;
    this.touchStartX = null;
    if (startX === null) return;
    const endX = e.changedTouches?.[0]?.clientX;
    if (typeof endX !== 'number') return;

    const dx = endX - startX;
    const threshold = 40;
    if (Math.abs(dx) < threshold) return;

    if (this.isRtl) {
      if (dx < 0) this.previous();
      else this.next();
    } else {
      if (dx < 0) this.next();
      else this.previous();
    }
  }

  trackLinkClick(linkType: string, href: string, linkItem?: string): void {
    this.cardLinkAnalytics.trackLinkClick(this.cardNo, linkType, href, linkItem);
  }

  getProductLabel(product: { productDetails?: Array<{ language?: string; title?: string }> }): string {
    return this.cardLinkAnalytics.getProductLabel(product, this.lang);
  }

  private restartAutoScroll(): void {
    this.stopAutoScroll();
    if ((this.products?.length ?? 0) <= this.itemsPerPage) {
      return;
    }
    this.autoScrollTimer = setInterval(() => this.next(), this.autoScrollMs);
  }

  private stopAutoScroll(): void {
    if (this.autoScrollTimer) {
      clearInterval(this.autoScrollTimer);
      this.autoScrollTimer = null;
    }
  }
}
